/**
 * useBillingCycles: fetch billing cycles + pending registrations.
 * Finanzas totals: ingresos = sum(paid cycles), pendientes = sum(pending cycles) + sum(pending registrations).
 */

import { useState, useEffect, useCallback } from "react";
import { fetchBillingCycles } from "../services/financeService";
import { listRegistrations } from "../../../lib/api";
import type {
  RawBillingCycleDoc,
  BillingCycleStatusDisplay,
  FinanzasTotals,
  PaymentGroupRow,
  RegistrationRecord,
  TsLike,
} from "../../../types";

function tsToMs(ts: TsLike | null | undefined): number | null {
  if (!ts) return null;
  if (typeof (ts as { toMillis?: () => number }).toMillis === "function") {
    return (ts as { toMillis: () => number }).toMillis();
  }
  const sec = (ts as { seconds?: number }).seconds ?? (ts as { _seconds?: number })._seconds;
  return sec != null ? sec * 1000 : null;
}

/** Full month label for Finanzas (e.g. "Abril 2026"). */
function formatMonthLabel(month: string): string {
  if (!month || month.length < 7) return "—";
  const [y, m] = month.split("-");
  const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
  const mi = parseInt(m, 10) - 1;
  if (Number.isNaN(mi) || mi < 0 || mi > 11) return month;
  return `${monthNames[mi]} ${y}`;
}

function registrationCreatedMonth(reg: RegistrationRecord): string {
  const ms = tsToMs(reg.createdAt);
  if (ms == null) return "";
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export interface UseBillingCyclesResult {
  /** Grouped by paymentGroupId (one row per payment transaction). */
  groups: PaymentGroupRow[];
  totals: FinanzasTotals;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useBillingCycles(): UseBillingCyclesResult {
  const [groups, setGroups] = useState<PaymentGroupRow[]>([]);
  const [totals, setTotals] = useState<FinanzasTotals>({
    totalPaidAmount: 0,
    totalPendingAmount: 0,
    countPaid: 0,
    countPending: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [billingRes, regsRes] = await Promise.all([
        fetchBillingCycles(),
        listRegistrations(),
      ]);
      const { billingCycles } = billingRes;
      const pendingRegistrations = (regsRes.registrations ?? []).filter(
        (r) => r.status === "awaiting_payment" || r.status === "awaiting_manual_payment"
      );

      const groupKey = (c: RawBillingCycleDoc) => (c.paymentGroupId && c.paymentGroupId.trim()) ? c.paymentGroupId : `single-${c.id}`;
      const byGroup = new Map<string, RawBillingCycleDoc[]>();
      for (const cycle of billingCycles) {
        const key = groupKey(cycle);
        if (!byGroup.has(key)) byGroup.set(key, []);
        byGroup.get(key)!.push(cycle);
      }

      const result: PaymentGroupRow[] = [];
      let totalPaidAmount = 0;
      let totalPendingAmount = 0;
      let countPaid = 0;
      let countPending = 0;

      byGroup.forEach((cycles, key) => {
        const first = cycles[0];
        const isPaid = first.status === "paid";
        const status: BillingCycleStatusDisplay = isPaid ? "Pagado" : "Pendiente de pago";
        const totalAmount = cycles.reduce((s, c) => s + (c.amount ?? 0), 0);
        const periodYYYYMM = first.month ?? (first.periodStart ? (() => {
          const ms = tsToMs(first.periodStart);
          if (ms == null) return "";
          const d = new Date(ms);
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        })() : "");
        const classNames = cycles.map((c) => c.classNameSnapshot ?? c.classId ?? "").filter(Boolean);
        const comprobanteDisplay = first.requireNCF && first.comprobanteType
          ? first.comprobanteType
          : "Consumidor Final";

        if (isPaid) {
          totalPaidAmount += totalAmount;
          countPaid += 1;
        } else {
          totalPendingAmount += totalAmount;
          countPending += 1;
        }

        result.push({
          id: key,
          studentId: first.studentId ?? "",
          studentName: first.studentName ?? first.studentId ?? "",
          classNames,
          periodYYYYMM,
          periodLabel: formatMonthLabel(periodYYYYMM),
          totalAmount,
          status,
          paymentMethod: first.paymentMethod,
          comprobanteDisplay,
        });
      });

      for (const reg of pendingRegistrations) {
        const periodYYYYMM = registrationCreatedMonth(reg);
        const totalAmount = reg.totalPrice ?? 0;
        totalPendingAmount += totalAmount;
        countPending += 1;
        result.push({
          id: `reg-pending-${reg.id}`,
          studentId: "",
          studentName: reg.child?.childName ?? "—",
          classNames: ["Inscripción pendiente"],
          periodYYYYMM,
          periodLabel: formatMonthLabel(periodYYYYMM),
          totalAmount,
          status: "Pendiente de pago",
          paymentMethodDisplay: reg.paymentMethod === "ONLINE" ? "En línea" : "En escuela",
          comprobanteDisplay: "—",
        });
      }

      setGroups(result);
      setTotals({
        totalPaidAmount,
        totalPendingAmount,
        countPaid,
        countPending,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al cargar finanzas.");
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { groups, totals, loading, error, refetch: load };
}
